import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  ApiKey,
  Consumer,
  CreateApiKeyInput,
  CreateApiKeyResponse,
  CreateConsumerInput,
} from '@/api/types'

const CONSUMERS_KEY = ['consumers'] as const
const apiKeysKey = (id: number) => ['api-keys', id] as const

export function useConsumers() {
  return useQuery({
    queryKey: CONSUMERS_KEY,
    queryFn: api.listConsumers,
  })
}

export function useCreateConsumer() {
  const qc = useQueryClient()
  return useMutation<Consumer, Error, CreateConsumerInput>({
    mutationFn: (input) => api.createConsumer(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONSUMERS_KEY })
    },
  })
}

export function useDeleteConsumer() {
  const qc = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => api.deleteConsumer(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONSUMERS_KEY })
    },
  })
}

export function useApiKeys(consumerId: number | null) {
  return useQuery({
    queryKey: consumerId ? apiKeysKey(consumerId) : ['api-keys', 'none'],
    queryFn: () => api.listApiKeys(consumerId as number),
    enabled: consumerId != null,
  })
}

export function useCreateApiKey(consumerId: number | null) {
  const qc = useQueryClient()
  return useMutation<CreateApiKeyResponse, Error, CreateApiKeyInput>({
    mutationFn: (input) => api.createApiKey(consumerId as number, input),
    onSuccess: () => {
      if (consumerId != null) {
        void qc.invalidateQueries({ queryKey: apiKeysKey(consumerId) })
      }
    },
  })
}

export function useRevokeApiKey(consumerId: number | null) {
  const qc = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => api.revokeApiKey(id),
    onSuccess: () => {
      if (consumerId != null) {
        void qc.invalidateQueries({ queryKey: apiKeysKey(consumerId) })
      }
    },
  })
}

export function useDeleteApiKey(consumerId: number | null) {
  const qc = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => api.deleteApiKey(id),
    onSuccess: () => {
      if (consumerId != null) {
        void qc.invalidateQueries({ queryKey: apiKeysKey(consumerId) })
      }
    },
  })
}

export type { Consumer, ApiKey }
